import { createSlice, PayloadAction, createAsyncThunk } from "@reduxjs/toolkit";
import { RootState } from "..";
import { TimeEntryRow } from "@/modules/TimeEntry/components/TimeEntryTable";
import { TimeEntry } from "@/types/time-entry.model";
import { createTimeEntryService } from "@/service/time-entry.service";
import {
  getProjectFilterListService,
  getServicesFilterListService,
} from "@/service/project.service";
import moment from "moment";
import { ROLES, removeEmptyProps } from "@/utils/constants";
import { selectSelectedOrganization } from "./clientOrganizationSlice";

// Define the state structure for the time entry module
interface TimeEntryState {
  teamMemberId: string;
  projectId: string;
  selectedProject: any;
  loading: boolean;
  error: string | null;
  projectOptions: any[];
  loadingProjectOptions: boolean;
  serviceOptions: any[];
  loadingServiceOptions: boolean;
}

// Initial state
const initialState: TimeEntryState = {
  teamMemberId: "",
  projectId: "",
  projectOptions: [],
  serviceOptions: [],
  loadingProjectOptions: false,
  loadingServiceOptions: false,
  selectedProject: null,
  loading: false,
  error: null,
};

// Helper function to convert TimeEntryRow to API TimeEntry
const mapRowToApiEntry = (
  row: TimeEntryRow,
  userId: string,
  projectId: string
): Omit<TimeEntry, "id"> => ({
  user_id: userId,
  project_id: projectId,
  clock_in_date: row.clockInDate
    ? moment(row.clockInDate, "YYYY-MM-DD").format("DD-MM-YYYY")
    : "",
  clock_in_time: row.clockInTime
    ? moment(row.clockInTime, "hh:mm A").format("HH:mm")
    : "",
  clock_out_date: row.clockOutDate
    ? moment(row.clockOutDate, "YYYY-MM-DD").format("DD-MM-YYYY")
    : "",
  clock_out_time: row.clockOutTime
    ? moment(row.clockOutTime, "hh:mm A").format("HH:mm")
    : "",
  duration: row.duration,
  is_billable: row.billable,
  service: row.serviceItem,
  entry: row.entry || "MANUAL",
  notes: row.notes,
});

export const fetchServicesFilterList = createAsyncThunk(
  "timeEntry/fetchServicesFilterList",
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await getServicesFilterListService();
      return data.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch services filter list"
      );
    }
  }
);

export const fetchProjectOptions = createAsyncThunk(
  "timeEntry/fetchProjectOptions",
  async (
    {
      search,
      userId,
      clientId,
    }: {
      search: string;
      userId?: string;
      clientId?: string;
    },
    { rejectWithValue }
  ) => {
    try {
      const { data } = await getProjectFilterListService({
        search,
        userId,
        clientId: clientId,
      });
      return data.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch project options"
      );
    }
  }
);

// We're not using fetch thunks as this is only for key-in, not for displaying old data

export const createTimeEntry = createAsyncThunk(
  "timeEntry/createTimeEntry",
  async (payload: TimeEntryRow, { getState, rejectWithValue }) => {
    try {
      const state = getState() as RootState;
      const { teamMemberId, projectId } = state.timeEntry;

      let apiEntry = mapRowToApiEntry(payload, teamMemberId, projectId);

      if (
        apiEntry.clock_in_date &&
        apiEntry.clock_in_time &&
        apiEntry.clock_out_date &&
        apiEntry.clock_out_time
      ) {
        delete apiEntry.duration;
      }

      const response = await createTimeEntryService(apiEntry as TimeEntry);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to create time entry"
      );
    }
  }
);

export const resetFilters = createAsyncThunk(
  "timeEntry/resetFilters",
  async (_, { dispatch, getState, rejectWithValue }) => {
    try {
      const state = getState() as RootState;
      const { isActive, activeEntry } = state.timer;

      if (isActive && activeEntry) {
        return;
      } else {
        dispatch(resetForm());
      }

      return;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to reset filters"
      );
    }
  }
);

// Create the slice
export const timeEntrySlice = createSlice({
  name: "timeEntry",
  initialState,
  reducers: {
    // Set team member
    setTeamMember: (state, action: PayloadAction<string>) => {
      state.teamMemberId = action.payload;
    },

    // Set project
    setProject: (
      state,
      action: PayloadAction<{ projectId: string; projectData: any }>
    ) => {
      state.projectId = action.payload.projectId;
      state.selectedProject = action.payload.projectData;
    },
    resetForm: (state) => {
      state.teamMemberId = "";
      state.projectId = "";
      state.selectedProject = null;
      state.loading = false;
      state.error = null;
    },
    resetProject: (state) => {
      state.projectId = "";
      state.selectedProject = null;
      state.loading = false;
      state.error = null;
    },
    // We're not using pagination as this is only for key-in
    resetAllState: () => initialState,
  },
  extraReducers: (builder) => {
    // Create time entry
    builder
      .addCase(createTimeEntry.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createTimeEntry.fulfilled, (state, action) => {
        state.loading = false;
      })
      .addCase(createTimeEntry.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(fetchProjectOptions.pending, (state) => {
        state.loadingProjectOptions = true;
        state.error = null;
      })
      .addCase(fetchProjectOptions.fulfilled, (state, action) => {
        state.projectOptions = action.payload;
        state.loadingProjectOptions = false;
      })
      .addCase(fetchProjectOptions.rejected, (state, action) => {
        state.loadingProjectOptions = false;
      })
      .addCase(fetchServicesFilterList.pending, (state) => {
        state.loadingServiceOptions = true;
        state.error = null;
      })
      .addCase(fetchServicesFilterList.fulfilled, (state, action) => {
        state.serviceOptions = action.payload;
        state.loadingServiceOptions = false;
      })
      .addCase(fetchServicesFilterList.rejected, (state, action) => {
        state.loadingServiceOptions = false;
      });
  },
});

// Export actions
export const {
  setTeamMember,
  setProject,
  resetForm,
  resetAllState,
  resetProject,
} = timeEntrySlice.actions;

// Export selectors
export const selectTimeEntryState = (state: RootState) => state.timeEntry;
export const selectTeamMemberId = (state: RootState) =>
  state.timeEntry.teamMemberId;
export const selectProjectId = (state: RootState) => state.timeEntry.projectId;
export const selectSelectedProject = (state: RootState) =>
  state.timeEntry.selectedProject;
export const selectTimeEntryLoading = (state: RootState) =>
  state.timeEntry.loading;
export const selectTimeEntryError = (state: RootState) => state.timeEntry.error;
// We're not using pagination as this is only for key-in

export const selectProjectOptions = (state: RootState) =>
  state.timeEntry.projectOptions;
export const selectLoadingProjectOptions = (state: RootState) =>
  state.timeEntry.loadingProjectOptions;
export const selectServiceOptions = (state: RootState) =>
  state.timeEntry.serviceOptions;
export const selectLoadingServiceOptions = (state: RootState) =>
  state.timeEntry.loadingServiceOptions;
// Export reducer
export default timeEntrySlice.reducer;
